import { useEffect, useState } from 'react';
import {
  feedingAreaService,
  type FeedingAreaItem,
  type CreateFeedingAreaRequest,
} from '../../services/feeding-area.service';
import { regionService, type RegionNodeDto } from '../../services/region.service';
import './AdminFeedingAreas.css';

type FilterActive = 'all' | 'active' | 'inactive';

const emptyForm: CreateFeedingAreaRequest = {
  name: '',
  manager: '',
  phoneNumber: '',
  regionId: null,
  accountNumber: '',
  productionCapacity: 0,
  occupancyPercentage: 0,
  iinBin: '',
  notes: '',
  isActive: true,
  isEasu: false,
  latitude: null,
  longitude: null,
};

export default function AdminFeedingAreas() {
  const [areas, setAreas] = useState<FeedingAreaItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterActive>('all');

  const [regions, setRegions] = useState<RegionNodeDto[]>([]);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<CreateFeedingAreaRequest>({ ...emptyForm });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    const isActive =
      filter === 'active' ? true : filter === 'inactive' ? false : undefined;
    feedingAreaService
      .getAll(isActive)
      .then((res) => {
        if (res.success && res.data) setAreas(res.data);
        else setError(res.message || 'Ошибка загрузки');
      })
      .catch(() => setError('Ошибка загрузки данных'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [filter]);

  useEffect(() => {
    regionService
      .getChildren(null)
      .then((res) => {
        if (res.success && res.data) setRegions(res.data);
      })
      .catch(() => {});
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setMessage(null);
    setShowForm(true);
  };

  const openEdit = (area: FeedingAreaItem) => {
    setEditingId(area.id);
    setForm({
      name: area.name,
      manager: area.manager,
      phoneNumber: area.phoneNumber,
      regionId: area.regionId ?? null,
      accountNumber: area.accountNumber,
      productionCapacity: area.productionCapacity,
      occupancyPercentage: Number(area.occupancyPercentage),
      iinBin: area.iinBin ?? '',
      notes: area.notes ?? '',
      isActive: area.isActive,
      isEasu: area.isEasu,
      latitude: area.latitude ?? null,
      longitude: area.longitude ?? null,
    });
    setMessage(null);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...emptyForm });
    setMessage(null);
  };

  const handleChange = (
    field: keyof CreateFeedingAreaRequest,
    value: string | number | boolean | null
  ) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setMessage({ type: 'err', text: 'Наименование обязательно' });
      return;
    }
    if (!form.manager.trim()) {
      setMessage({ type: 'err', text: 'Руководитель обязателен' });
      return;
    }
    if (!form.phoneNumber.trim()) {
      setMessage({ type: 'err', text: 'Номер телефона обязателен' });
      return;
    }
    if (!form.accountNumber.trim()) {
      setMessage({ type: 'err', text: 'Учетный номер обязателен' });
      return;
    }
    if (form.productionCapacity <= 0) {
      setMessage({ type: 'err', text: 'Производственная мощность должна быть больше 0' });
      return;
    }

    setSubmitLoading(true);
    setMessage(null);

    const payload: CreateFeedingAreaRequest = {
      ...form,
      iinBin: form.iinBin?.trim() || null,
      notes: form.notes?.trim() || null,
      regionId: form.regionId || null,
      latitude: form.latitude || null,
      longitude: form.longitude || null,
    };

    const request =
      editingId !== null
        ? feedingAreaService.update(editingId, payload)
        : feedingAreaService.create(payload);

    request
      .then((res) => {
        if (res.success) {
          setMessage({
            type: 'ok',
            text: editingId !== null ? 'Обновлено успешно' : 'Создано успешно',
          });
          load();
          if (editingId === null) setForm({ ...emptyForm });
        } else {
          setMessage({ type: 'err', text: res.message || 'Ошибка сохранения' });
        }
      })
      .catch(() => setMessage({ type: 'err', text: 'Ошибка сети' }))
      .finally(() => setSubmitLoading(false));
  };

  const handleDelete = (id: number) => {
    if (!confirm('Удалить откорм площадку?')) return;
    feedingAreaService
      .delete(id)
      .then((res) => {
        if (res.success) load();
      })
      .catch(() => {});
  };

  return (
    <div className="admin-feedarea">
      <div className="admin-feedarea-header">
        <div>
          <h1>Откорм площадки</h1>
          <p className="admin-feedarea-desc">Управление откорм площадками</p>
        </div>
        <button className="admin-feedarea-add-btn" onClick={showForm ? closeForm : openCreate}>
          {showForm ? 'Отмена' : '+ Добавить'}
        </button>
      </div>

      {showForm && (
        <form className="admin-feedarea-form" onSubmit={handleSubmit}>
          <h2 className="admin-feedarea-form-title">
            {editingId !== null ? 'Редактировать площадку' : 'Новая площадка'}
          </h2>
          <div className="admin-feedarea-form-grid">
            <div className="admin-feedarea-row">
              <label>Наименование *</label>
              <input
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="Наименование хозяйства"
                maxLength={500}
              />
            </div>
            <div className="admin-feedarea-row">
              <label>Руководитель *</label>
              <input
                value={form.manager}
                onChange={(e) => handleChange('manager', e.target.value)}
                placeholder="ФИО руководителя"
                maxLength={200}
              />
            </div>
            <div className="admin-feedarea-row">
              <label>Телефон *</label>
              <input
                value={form.phoneNumber}
                onChange={(e) => handleChange('phoneNumber', e.target.value)}
                placeholder="+7 (700) 000-00-00"
                maxLength={20}
              />
            </div>
            <div className="admin-feedarea-row">
              <label>Учетный номер *</label>
              <input
                value={form.accountNumber}
                onChange={(e) => handleChange('accountNumber', e.target.value)}
                placeholder="Учетный номер"
                maxLength={100}
              />
            </div>
            <div className="admin-feedarea-row">
              <label>Производственная мощность (скотомест) *</label>
              <input
                type="number"
                min={1}
                value={form.productionCapacity || ''}
                onChange={(e) => handleChange('productionCapacity', Number(e.target.value))}
                placeholder="0"
              />
            </div>
            <div className="admin-feedarea-row">
              <label>Загруженность (%)</label>
              <input
                type="number"
                min={0}
                max={100}
                step={0.01}
                value={form.occupancyPercentage || ''}
                onChange={(e) => handleChange('occupancyPercentage', Number(e.target.value))}
                placeholder="0"
              />
            </div>
            <div className="admin-feedarea-row">
              <label>ИИН/БИН</label>
              <input
                value={form.iinBin ?? ''}
                onChange={(e) => handleChange('iinBin', e.target.value)}
                placeholder="ИИН или БИН"
                maxLength={12}
              />
            </div>
            <div className="admin-feedarea-row">
              <label>Регион</label>
              <select
                value={form.regionId ?? ''}
                onChange={(e) =>
                  handleChange('regionId', e.target.value ? Number(e.target.value) : null)
                }
              >
                <option value="">— не выбран —</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nameRu}
                  </option>
                ))}
              </select>
            </div>
            <div className="admin-feedarea-row">
              <label>Широта</label>
              <input
                type="number"
                step="any"
                value={form.latitude ?? ''}
                onChange={(e) =>
                  handleChange('latitude', e.target.value ? Number(e.target.value) : null)
                }
                placeholder="Широта"
              />
            </div>
            <div className="admin-feedarea-row">
              <label>Долгота</label>
              <input
                type="number"
                step="any"
                value={form.longitude ?? ''}
                onChange={(e) =>
                  handleChange('longitude', e.target.value ? Number(e.target.value) : null)
                }
                placeholder="Долгота"
              />
            </div>
            <div className="admin-feedarea-row admin-feedarea-row--full">
              <label>Примечание</label>
              <textarea
                value={form.notes ?? ''}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Примечание"
                rows={3}
                maxLength={1000}
              />
            </div>
            <div className="admin-feedarea-row admin-feedarea-row--checks">
              <label className="admin-feedarea-check">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => handleChange('isActive', e.target.checked)}
                />
                Активна
              </label>
              <label className="admin-feedarea-check">
                <input
                  type="checkbox"
                  checked={form.isEasu}
                  onChange={(e) => handleChange('isEasu', e.target.checked)}
                />
                ЕАСУ
              </label>
            </div>
          </div>

          {message && (
            <p className={message.type === 'ok' ? 'admin-feedarea-msg-ok' : 'admin-feedarea-msg-err'}>
              {message.text}
            </p>
          )}

          <div className="admin-feedarea-actions">
            <button type="submit" disabled={submitLoading}>
              {submitLoading ? 'Сохранение...' : editingId !== null ? 'Сохранить' : 'Создать'}
            </button>
            <button type="button" className="admin-feedarea-cancel-btn" onClick={closeForm}>
              Отмена
            </button>
          </div>
        </form>
      )}

      <div className="admin-feedarea-filters">
        {(['all', 'active', 'inactive'] as FilterActive[]).map((f) => (
          <button
            key={f}
            className={`admin-feedarea-filter${filter === f ? ' admin-feedarea-filter--active' : ''}`}
            onClick={() => setFilter(f)}
          >
            {f === 'all' ? 'Все' : f === 'active' ? 'Активные' : 'Неактивные'}
          </button>
        ))}
      </div>

      {loading && <p className="admin-feedarea-loading">Загрузка...</p>}
      {!loading && error && <p className="admin-feedarea-error">{error}</p>}
      {!loading && !error && (
        <div className="admin-feedarea-table-wrap">
          <table className="admin-feedarea-table">
            <thead>
              <tr>
                <th>Наименование</th>
                <th>Руководитель</th>
                <th>Телефон</th>
                <th>Регион</th>
                <th>Мощность</th>
                <th>Загруженность</th>
                <th>Активна</th>
                <th>ЕАСУ</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {areas.map((area) => (
                <tr key={area.id}>
                  <td>{area.name}</td>
                  <td>{area.manager}</td>
                  <td>{area.phoneNumber}</td>
                  <td>{area.region?.nameRu ?? '—'}</td>
                  <td>{area.productionCapacity}</td>
                  <td>{area.occupancyPercentage}%</td>
                  <td>
                    <span className={`admin-feedarea-badge${area.isActive ? ' admin-feedarea-badge--yes' : ' admin-feedarea-badge--no'}`}>
                      {area.isActive ? 'Да' : 'Нет'}
                    </span>
                  </td>
                  <td>
                    <span className={`admin-feedarea-badge${area.isEasu ? ' admin-feedarea-badge--yes' : ' admin-feedarea-badge--no'}`}>
                      {area.isEasu ? 'Да' : 'Нет'}
                    </span>
                  </td>
                  <td className="admin-feedarea-row-actions">
                    <button
                      className="admin-feedarea-edit-btn"
                      onClick={() => openEdit(area)}
                    >
                      Изменить
                    </button>
                    <button
                      className="admin-feedarea-del-btn"
                      onClick={() => handleDelete(area.id)}
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {areas.length === 0 && (
            <p className="admin-feedarea-empty">Нет откорм площадок</p>
          )}
        </div>
      )}
    </div>
  );
}
